import { useCallback, useEffect, useState } from 'react';
import type { CuentaCobro, ListaPoliticas, Politica, UsoPolitica, VersionPolitica } from '../../../api/ops/pagos';
import { ErrorApi } from '../../../api/problema';
import { useAvisoBreve } from '../../../componentes/avisoBreve';
import { Chip } from '../../../componentes/Chip';
import { Deshacer } from '../../../componentes/Deshacer';
import { EstadoCargando, EstadoError, EstadoNoContratado, EstadoSinPermiso } from '../../../componentes/EstadoPagina';
import { Filtros } from '../../../componentes/Filtros';
import { Icono } from '../../../componentes/Icono';
import type { NombreIcono } from '../../../componentes/iconos';
import { Paginacion } from '../../../componentes/Paginacion';
import { PanelLateral } from '../../../componentes/PanelLateral';
import { Ventana } from '../../../componentes/Ventana';
import { rolesDe } from '../../../logica/acceso';
import { dinero } from '../../../logica/dinero';
import { useEnvioRetrasado } from '../../../logica/envioRetrasado';
import { avisoBreveDeError, estadoDeError } from '../../../logica/estados';
import { diaMes } from '../../../logica/fechasCortas';
import { haySiguiente, usePaginacionCursor } from '../../../logica/paginacion';
import { useCabecera, type AccionCabecera } from '../../../marco/cabecera';
import { usePermisos } from '../../../permisos/permisos';
import type { PropsPantalla } from '../../tipos';
import { cargarSitios, nombreDeSitio, useNegocioPagos, type Sitios } from '../negocio';
import { AYUDA, FORMA_CALCULO, PERMISOS, VARIOS_SERVICIOS, horas, textoDeError } from '../textos';
import { alReservar, borradorDe, condicionesDe, siCancelaTarde } from './condiciones';
import { Editor, type Edicion } from './Editor';

/**
 * Políticas de cobro (4.2): tabla (tarjetas en móvil) con lo que se pide al reservar y lo que se cobra si cancela tarde o
 * no viene; filtros «En uso · Archivadas» sin cifras (G140). Ficha en RV Panel con las condiciones, «Así lo ve el
 * cliente» con `conditions_text` tal cual (D-004), dónde se aplica y las versiones. Crear, cambiar (versión nueva con
 * `If-Match`), renombrar (G279), duplicar y archivar. Guardar una versión y archivar llevan «Deshacer» 5 s (G159: no
 * mueven dinero); una política en uso no se archiva (`409` con `usages`). Sin cuenta de cobro: «Primero, la cuenta de
 * cobro»; sin el bloque: «No contratado» (`403 entitlement_required`).
 */
type Abierta = { tipo: 'ficha'; id: string } | { tipo: 'editor'; edicion: Edicion } | null;

const FILTROS = [{ etiqueta: 'En uso' }, { etiqueta: 'Archivadas' }] as const;

interface Ficha {
    politica: Politica;
    version: string | null;
    usos: UsoPolitica[] | null;
    versiones: VersionPolitica[] | null;
}

export function Politicas(props: PropsPantalla) {
    const { ops, negocio, movil, soloLectura, membresia, nombreNegocio, navegar, ruta } = props;
    const permisos = usePermisos();
    const avisar = useAvisoBreve();
    const puede = permisos.puede(PERMISOS.politicas);
    const editable = puede && !soloLectura;
    const n = useNegocioPagos(ops, negocio);
    const envio = useEnvioRetrasado();

    const [filtro, setFiltro] = useState(0);
    const pag = usePaginacionCursor();
    const [cargada, setCargada] = useState<{ vista: string; lista: ListaPoliticas | null; error: unknown } | null>(null);
    const [cuenta, setCuenta] = useState<CuentaCobro | null | 'sin' | 'desconocida'>(null);
    const [intento, setIntento] = useState(0);
    const [abierta, setAbierta] = useState<Abierta>(null);
    const [fichaLeida, setFicha] = useState<Ficha | null>(null);
    const [sitios, setSitios] = useState<Sitios | null>(null);
    const [enUso, setEnUso] = useState<{ politica: string; usos: UsoPolitica[] } | null>(null);
    const [ocultas, setOcultas] = useState<ReadonlySet<string>>(new Set());
    const [recargaFicha, setRecargaFicha] = useState(0);
    const { reiniciar } = pag;

    const recargar = useCallback(() => {
        setIntento((i) => i + 1);
    }, []);

    useEffect(() => {
        if (!puede) {
            return;
        }
        let vivo = true;
        ops.getPaymentAccount(negocio)
            .then((r) => {
                if (vivo) {
                    setCuenta(r.datos);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setCuenta(e instanceof ErrorApi && e.estado === 404 ? 'sin' : 'desconocida');
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, puede]);

    const archivadas = filtro === 1;
    useEffect(() => {
        if (!puede) {
            return;
        }
        let vivo = true;
        const vista = `${String(filtro)}|${pag.cursor ?? ''}`;
        ops.listPaymentPolicies(negocio, { cursor: pag.cursor, status: archivadas ? 'archived' : undefined })
            .then((l) => {
                if (vivo) {
                    setCargada({ vista, lista: l, error: null });
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setCargada((c) => ({ vista, lista: c?.vista === vista ? c.lista : null, error: e }));
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, puede, filtro, archivadas, pag.cursor, intento]);

    // La ficha: la política con su versión (ETag), dónde se aplica y sus versiones.
    const idFicha = abierta?.tipo === 'ficha' ? abierta.id : null;
    useEffect(() => {
        if (idFicha === null) {
            return;
        }
        let vivo = true;
        ops.getPaymentPolicy(negocio, idFicha)
            .then((r) => {
                if (!vivo) {
                    return;
                }
                setFicha({ politica: r.datos, version: r.version, usos: null, versiones: null });
                void Promise.all([
                    ops.listPaymentPolicyUsages(negocio, idFicha, { limit: 100 }).then((u) => u.data).catch(() => []),
                    ops.listPaymentPolicyVersions(negocio, idFicha, { limit: 50 }).then((v) => v.data).catch(() => []),
                ]).then(([usos, versiones]) => {
                    if (vivo) {
                        setFicha((f) => (f === null || f.politica.id !== idFicha ? f : { ...f, usos, versiones }));
                    }
                });
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setAbierta(null);
                    avisar({ tipo: 'error', texto: textoDeError(e, 'No he podido abrir la política. Prueba de nuevo.') });
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, idFicha, recargaFicha, avisar]);

    // Lo leído solo vale para la política abierta (al abrir otra, «Cargando…» hasta que llega).
    const ficha = fichaLeida !== null && fichaLeida.politica.id === idFicha ? fichaLeida : null;

    // Nombres de los sitios (de Reservas y Citas), solo cuando hacen falta.
    const necesitaSitios = idFicha !== null || enUso !== null;
    useEffect(() => {
        if (!necesitaSitios || sitios !== null) {
            return;
        }
        let vivo = true;
        void cargarSitios(ops, negocio).then((s) => {
            if (vivo) {
                setSitios(s);
            }
        });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, necesitaSitios, sitios]);

    const sinCuenta = cuenta === 'sin' || (cuenta !== null && typeof cuenta === 'object' && cuenta.status === 'pending');
    const acciones: AccionCabecera[] = [];
    if (puede && !sinCuenta) {
        acciones.push({ clave: 'nueva', texto: 'Nueva política', icono: 'plus', desactivado: soloLectura, onClick: () => { setAbierta({ tipo: 'editor', edicion: { modo: 'nueva', borrador: borradorDe('', null) } }); } });
    }
    useCabecera({ ayuda: puede ? AYUDA.politicas : [], acciones });

    const irACuenta = () => {
        navegar(`${ruta}?apartado=cuenta`);
    };
    const irAAsignar = () => {
        navegar(`${ruta}?apartado=asignacion`);
    };

    if (!puede) {
        return <EstadoSinPermiso seccion="Políticas" rol={membresia === undefined ? '' : rolesDe(membresia)} negocio={nombreNegocio} onInicio={() => { navegar('/'); }} movil={movil} />;
    }
    const vistaActual = `${String(filtro)}|${pag.cursor ?? ''}`;
    const lista = cargada?.vista === vistaActual ? cargada.lista : null;
    const error = cargada?.vista === vistaActual ? cargada.error : null;
    if (error !== null && lista === null) {
        const e = estadoDeError(error);
        if (e.tipo === 'no-contratado') {
            return (
                <EstadoNoContratado
                    bloque="Pagos y anti no-show"
                    incluye={['Políticas de cobro con señal y tarjeta de garantía', 'Cargos por no-show y cancelación tardía', 'Dónde se aplica cada política']}
                    propietario={membresia?.is_owner === true}
                    onAnadir={null}
                    onInicio={() => { navegar('/'); }}
                    movil={movil}
                />
            );
        }
        if (e.tipo === 'sin-permiso') {
            return <EstadoSinPermiso seccion="Políticas" rol={membresia === undefined ? '' : rolesDe(membresia)} negocio={nombreNegocio} onInicio={() => { navegar('/'); }} movil={movil} />;
        }
        return <EstadoError seccion="las políticas" codigoSoporte={e.codigoSoporte} onReintentar={recargar} movil={movil} />;
    }
    if (sinCuenta) {
        return (
            <div className="rv-ca-vacia">
                <span className="rv-ca-vacia__baldosa"><Icono nombre="wallet" /></span>
                <strong>Primero, la cuenta de cobro</strong>
                <span>Las políticas cobran señales y guardan tarjetas en tu cuenta de Stripe. Conéctala y vuelve aquí.</span>
                <button type="button" className="rv-b rv-b-pri" onClick={irACuenta}>
                    <Icono nombre="arrow-right" />
                    <span>Ir a Cuenta de cobro</span>
                </button>
            </div>
        );
    }
    if (lista === null) {
        return <EstadoCargando seccion="las políticas" />;
    }

    // ---- Acciones ----
    const guardar = (ed: Edicion, nombre: string) => {
        const conVarios = n.citas;
        const terms = condicionesDe(ed.borrador, n.moneda, conVarios);
        if (ed.modo === 'nueva') {
            return ops.createPaymentPolicy(negocio, nombre, terms).then(() => {
                setAbierta(null);
                avisar({ tipo: 'ok', texto: `Política «${nombre}» creada.` });
                recargar();
            });
        }
        const p = ed.politica;
        const version = ed.version ?? '';
        const cambiaNombre = nombre !== p.name;
        const cambianCondiciones = JSON.stringify(borradorDe('', p.current_version.terms)) !== JSON.stringify({ ...ed.borrador, nombre: '' });
        setAbierta({ tipo: 'ficha', id: p.id });
        envio.programar({
            texto: cambianCondiciones ? `Guardada la versión ${String(p.current_version.number + 1)} de «${nombre}».` : `«${nombre}» renombrada.`,
            donde: p.id,
            enviar: () => {
                const renombrar = cambiaNombre ? ops.updatePaymentPolicy(negocio, p.id, nombre, version).then((r) => r.version ?? version) : Promise.resolve(version);
                renombrar
                    .then((v) => (cambianCondiciones ? ops.createPaymentPolicyVersion(negocio, p.id, terms, v).then(() => undefined) : undefined))
                    .then(() => {
                        recargar();
                        setRecargaFicha((x) => x + 1);
                    })
                    .catch((e: unknown) => {
                        avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? textoDeError(e, 'No he podido guardar la política. Prueba de nuevo.') });
                        recargar();
                    });
            },
        });
        return Promise.resolve();
    };

    const archivar = (f: Ficha) => {
        const p = f.politica;
        if (p.usage_count > 0) {
            setEnUso({ politica: p.name, usos: f.usos ?? [] });
            return;
        }
        setAbierta(null);
        setOcultas((o) => new Set([...o, p.id]));
        envio.programar({
            texto: `«${p.name}» archivada.`,
            donde: 'archivar',
            alDeshacer: () => {
                setOcultas((o) => new Set([...o].filter((x) => x !== p.id)));
            },
            enviar: () => {
                ops.deletePaymentPolicy(negocio, p.id, f.version ?? '')
                    .then(recargar)
                    .catch((e: unknown) => {
                        setOcultas((o) => new Set([...o].filter((x) => x !== p.id)));
                        if (e instanceof ErrorApi && e.estado === 409) {
                            const usos = (e.problema as { usages?: UsoPolitica[] }).usages ?? [];
                            setEnUso({ politica: p.name, usos });
                            return;
                        }
                        avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? textoDeError(e, 'No he podido archivarla. Prueba de nuevo.') });
                    });
            },
        });
    };

    const filas = lista.data.filter((p) => !ocultas.has(p.id) && (archivadas ? p.status === 'archived' : p.status !== 'archived'));
    const ahora = new Date();

    return (
        <div className="rv-ca-apartado">
            <Filtros etiqueta="Políticas" items={FILTROS} valor={filtro} onCambio={(i) => { setFiltro(i); reiniciar(); }} />
            {typeof cuenta === 'object' && cuenta !== null && (cuenta.status === 'disconnected' || cuenta.status === 'restricted') && (
                <div className="rv-ca-caja rv-ca-caja--aviso"><div className="rv-ca-caja__fila"><Icono nombre="triangle-alert" /><span>{cuenta.status === 'disconnected' ? 'Las políticas están suspendidas porque la cuenta de cobro está desconectada. Vuelven al conectar otra cuenta.' : 'Stripe ha parado los cobros: mientras tanto, las políticas no cobran.'}</span></div></div>
            )}
            {!editable && <p className="rv-ca-nota"><Icono nombre="eye" /><span>El negocio está en solo lectura: puedes ver las políticas, pero no cambiarlas.</span></p>}
            {envio.pendiente !== null && (
                <div className="rv-ca-hecho" role="status">
                    <span className="rv-ca-hecho__baldosa"><Icono nombre="check" /></span>
                    <span style={{ flex: 1 }}>{envio.pendiente.texto}</span>
                    <Deshacer onDeshacer={envio.deshacer} clave={envio.pendiente.clave} duracion={envio.duracion} movil={movil} />
                </div>
            )}
            {filas.length === 0 ? (
                <div className="rv-ca-vacia">
                    <span className="rv-ca-vacia__baldosa"><Icono nombre={archivadas ? 'archive' : 'shield-check'} /></span>
                    <strong>{archivadas ? 'Ninguna política archivada' : 'Todavía no hay políticas'}</strong>
                    <span>{archivadas ? 'Las que archives se conservan aquí para las reservas que las usaron.' : 'Crea una con «Nueva política»: señal, tarjeta de garantía y cargos por no-show.'}</span>
                </div>
            ) : (
                <div className="rv-ca-lista" role="list" aria-label="Políticas de cobro">
                    {!movil && (
                        <div className="rv-pg-pol rv-pg-pol--cab" aria-hidden="true">
                            <span>Política</span>
                            <span>Al reservar</span>
                            <span>Si cancela tarde o no viene</span>
                            <span />
                        </div>
                    )}
                    {filas.map((p) => {
                        const t = p.current_version.terms;
                        const a = alReservar(t);
                        const c = siCancelaTarde(t);
                        const linea = [`Versión ${String(p.current_version.number)}`, `desde el ${diaMes(p.current_version.created_at, ahora)}`, p.usage_count > 0 ? (p.usage_count === 1 ? 'en 1 sitio' : `en ${String(p.usage_count)} sitios`) : ''].filter((x) => x !== '').join(' · ');
                        return (
                            <div
                                key={p.id}
                                role="listitem"
                                tabIndex={0}
                                className={`rv-pg-pol${movil ? ' rv-pg-pol--movil' : ''}`}
                                aria-label={`${p.name}. Abrir`}
                                onClick={() => { setAbierta({ tipo: 'ficha', id: p.id }); }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        setAbierta({ tipo: 'ficha', id: p.id });
                                    }
                                }}
                            >
                                <span className="rv-ca-fila__texto">
                                    <strong>{p.name}</strong>
                                    <span className="rv-pg-pol__linea">
                                        {p.status === 'suspended' && <Chip estado="pending">Suspendida</Chip>}
                                        {p.status !== 'archived' && p.usage_count === 0 && <Chip estado="pending">Sin asignar</Chip>}
                                        <span>{p.status === 'archived' && p.archived_at != null ? `Archivada el ${diaMes(p.archived_at, ahora)}` : linea}</span>
                                    </span>
                                </span>
                                <span className="rv-ca-fila__texto"><strong>{a.titulo}</strong><span>{a.sub}</span></span>
                                <span className="rv-ca-fila__texto"><strong>{c.titulo}</strong><span>{c.sub}</span></span>
                                <span className="rv-pg-pol__acciones">
                                    {editable && p.status !== 'archived' && (
                                        <button
                                            type="button"
                                            className="rv-bi rv-ca-fantasma"
                                            aria-label={`Duplicar ${p.name}`}
                                            title="Duplicar"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setAbierta({ tipo: 'editor', edicion: { modo: 'nueva', borrador: borradorDe(`Copia de ${p.name}`, t) } });
                                            }}
                                        >
                                            <Icono nombre="copy" />
                                        </button>
                                    )}
                                    <Icono nombre="chevron-right" className="rv-ca-chevron" />
                                </span>
                            </div>
                        );
                    })}
                </div>
            )}
            <Paginacion hayAnterior={pag.hayAnterior} haySiguiente={haySiguiente(lista.next_cursor)} onAnterior={pag.anterior} onSiguiente={() => { pag.siguiente(lista.next_cursor); }} movil={movil} />

            <PanelLateral
                abierto={abierta?.tipo === 'ficha'}
                onCerrar={() => { setAbierta(null); }}
                movil={movil}
                tamano="ficha"
                titulo={ficha?.politica.name ?? 'Política'}
                eyebrow="Política de cobro"
                subtitulo={ficha === null ? undefined : `Versión ${String(ficha.politica.current_version.number)} · desde el ${diaMes(ficha.politica.current_version.created_at)}`}
                icono="shield-check"
                accion={editable && ficha !== null && ficha.politica.status !== 'archived' ? 'Cambiar' : ''}
                accionIcono="pencil"
                onAccion={() => {
                    if (ficha !== null) {
                        setAbierta({ tipo: 'editor', edicion: { modo: 'version', politica: ficha.politica, version: ficha.version, borrador: borradorDe(ficha.politica.name, ficha.politica.current_version.terms) } });
                    }
                }}
            >
                {ficha === null ? (
                    <EstadoCargando seccion="la política" />
                ) : (
                    <FichaPolitica
                        ficha={ficha}
                        sitios={sitios}
                        n={n}
                        editable={editable}
                        onArchivar={() => { archivar(ficha); }}
                        onDuplicar={() => {
                            setAbierta({ tipo: 'editor', edicion: { modo: 'nueva', borrador: borradorDe(`Copia de ${ficha.politica.name}`, ficha.politica.current_version.terms) } });
                        }}
                        onAsignar={irAAsignar}
                    />
                )}
            </PanelLateral>

            <Editor ops={ops} negocio={negocio} movil={movil} abierta={abierta?.tipo === 'editor' ? abierta.edicion : null} n={n} onCerrar={() => { setAbierta(null); }} onGuardar={guardar} />

            <Ventana
                abierta={enUso !== null}
                titulo={enUso === null ? '' : `«${enUso.politica}» está asignada`}
                texto="Quítala de estos sitios o asígnales otra política antes de archivarla."
                accion="Cambiar dónde se aplica"
                accionIcono="arrow-right"
                cancelar="Cerrar"
                movil={movil}
                onAccion={() => {
                    setEnUso(null);
                    setAbierta(null);
                    irAAsignar();
                }}
                onCerrar={() => { setEnUso(null); }}
            >
                <div className="rv-ca-ventana-lineas">
                    {(enUso?.usos ?? []).map((u) => {
                        const s = nombreDeSitio(sitios, u.assignable.type, u.assignable.id, n);
                        return (
                            <div key={`${u.assignable.type}${u.assignable.id}`} className="rv-ca-ventana-linea">
                                <strong style={{ textAlign: 'left' }}>{s.nombre}</strong>
                                <span>{s.tipo}</span>
                            </div>
                        );
                    })}
                </div>
            </Ventana>
        </div>
    );
}

function FichaPolitica({ ficha, sitios, n, editable, onArchivar, onDuplicar, onAsignar }: { ficha: Ficha; sitios: Sitios | null; n: ReturnType<typeof useNegocioPagos>; editable: boolean; onArchivar: () => void; onDuplicar: () => void; onAsignar: () => void }) {
    const p = ficha.politica;
    const t = p.current_version.terms;
    const base = FORMA_CALCULO[t.calculation_basis][1];
    const plazo = t.late_cancellation_window_hours ?? 0;
    const lineas: [NombreIcono, string, string, string][] = [
        ['calculator', 'Forma de cálculo', FORMA_CALCULO[t.calculation_basis][0], ''],
        ['credit-card', 'Tarjeta de garantía', t.guarantee_required ? 'Sí, se guarda al reservar' : 'No', ''],
        ['banknote', 'Señal', t.deposit !== undefined && t.deposit.amount > 0 ? `${dinero(t.deposit)} ${base}` : 'Sin señal', t.deposit !== undefined && t.deposit.amount > 0 && plazo > 0 ? `Se devuelve si cancela con más de ${horas(plazo)}` : ''],
        ['user-x', 'Cargo por no-show', t.no_show_fee !== undefined && t.no_show_fee.amount > 0 ? `${dinero(t.no_show_fee)} ${base}` : 'Nada', t.no_show_fee !== undefined && t.no_show_fee.amount > 0 ? 'Cuando tu equipo marca el no-show' : ''],
        ['clock', 'Cancelación tardía', t.late_cancellation_fee !== undefined && t.late_cancellation_fee.amount > 0 ? `${dinero(t.late_cancellation_fee)} ${base}` : 'Nada', t.late_cancellation_fee !== undefined && t.late_cancellation_fee.amount > 0 ? `Con menos de ${horas(plazo)} de antelación` : plazo > 0 ? `Gratis hasta ${horas(plazo)} antes` : ''],
        // G41: solo en un negocio con Citas.
        ...(n.citas ? [['layers', 'Citas de varios servicios', VARIOS_SERVICIOS[t.multi_service_rule ?? 'single_service'], ''] as [NombreIcono, string, string, string]] : []),
    ];
    return (
        <div className="rv-ca-cuerpo">
            {p.status !== 'active' && (
                <div className="rv-ca-caja"><div className="rv-ca-caja__fila"><Icono nombre={p.status === 'archived' ? 'archive' : 'unplug'} /><span>{p.status === 'archived' ? `Archivada${p.archived_at == null ? '' : ` el ${diaMes(p.archived_at)}`}. Ya no se asigna, pero las reservas que la usaron la siguen citando.` : 'Suspendida porque la cuenta de cobro está desconectada. Vuelve al conectar otra cuenta.'}</span></div></div>
            )}
            <div className="rv-ca-bloque">
                {lineas.map(([icono, k, v, sub]) => (
                    <div key={k} className="rv-ca-dato">
                        <span className="rv-pg-dato-k"><Icono nombre={icono} />{k}</span>
                        <span className="rv-pg-dato-v"><strong>{v}</strong>{sub !== '' && <small>{sub}</small>}</span>
                    </div>
                ))}
            </div>
            <div className="rv-ca-bloque">
                <p className="rv-ca-ceja">Así lo ve el cliente</p>
                <div className="rv-pg-condiciones">{p.current_version.conditions_text}</div>
                <span className="rv-ca-nota rv-ca-nota--gris">El mismo texto en la web, el widget, el chat y la confirmación.</span>
            </div>
            <div className="rv-ca-bloque">
                <p className="rv-ca-ceja">Dónde se aplica</p>
                {ficha.usos === null ? (
                    <span className="rv-ca-nota rv-ca-nota--gris">Un momento…</span>
                ) : ficha.usos.length === 0 ? (
                    <span className="rv-ca-nota rv-ca-nota--gris">Sin asignar: todavía no se aplica en ningún sitio.</span>
                ) : (
                    ficha.usos.map((u) => {
                        const s = nombreDeSitio(sitios, u.assignable.type, u.assignable.id, n);
                        return (
                            <div key={`${u.assignable.type}${u.assignable.id}`} className="rv-ca-dato">
                                <span>{s.tipo}</span>
                                <strong>{s.nombre}</strong>
                            </div>
                        );
                    })
                )}
                {editable && p.status !== 'archived' && (
                    <span>
                        <button type="button" className="rv-b rv-b-sec" onClick={onAsignar}><Icono nombre="list-checks" /><span>Cambiar dónde se aplica</span></button>
                    </span>
                )}
            </div>
            <details className="rv-pg-plegable rv-pg-plegable--plano">
                <summary>
                    <span className="rv-ca-fila__texto"><strong>Versiones</strong><span>Cada cambio crea una versión; lo ya reservado sigue con la suya.</span></span>
                    <Icono nombre="chevron-down" className="rv-pg-plegable__flecha" />
                </summary>
                {(ficha.versiones ?? []).map((v) => (
                    <div key={v.id} className="rv-ca-dato">
                        <span>{`Versión ${String(v.number)}`}</span>
                        <strong>{`desde el ${diaMes(v.created_at)}`}</strong>
                    </div>
                ))}
            </details>
            {editable && p.status !== 'archived' && (
                <div className="rv-pg-botones">
                    <button type="button" className="rv-b rv-b-sec" onClick={onDuplicar}><Icono nombre="copy" /><span>Duplicar</span></button>
                    <button type="button" className="rv-b rv-b-sec rv-b-rojo" onClick={onArchivar}><Icono nombre="archive" /><span>Archivar</span></button>
                </div>
            )}
        </div>
    );
}
